import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { ContactList } from '@client/components/contacts/ContactList';
import { SupplierDetails as SupplierDetailsOriginal } from '@client/components/suppliers/SupplierDetails';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute } from '@tanstack/react-router';
import { UserPlus } from 'lucide-react';

export const Route = createFileRoute('/console/$orgId/configuration/vendor-management/suppliers/list/details/$id')({
    staticData: {
        breadcrumb: {
            title: 'Supplier details',
        },
        presentation: 'page',
        ui: {
            widthVariant: 'default',
        },
    },
    params: {
        parse({ id }) {
            return {
                id: BigInt(id),
            };
        },
    },
    beforeLoad: ({ context, params }): void => {
        // Warm the supplier query while the route chunk parses so the detail cards paint without
        // waiting on a render-time fetch in SupplierDetails (which otherwise shows a skeleton until
        // the query resolves). Fire-and-forget; the render-time useQuery retries on a miss.
        void context.trpcUtils.supplier.getSupplier.prefetch({ id: params.id });
    },
    component: SupplierDetails,
});

function SupplierDetails() {
    const { id } = Route.useParams();
    const orgId = useOrgId();
    const navigate = useNavigate();

    return (
        <PageController route={Route}>
            <SupplierDetailsOriginal id={id} />
            <ContactList
                type="SUPPLIER"
                entity="supplier"
                entityId={id}
                title="Supplier contacts"
                rightAddon={
                    <Button.Link
                        variant="secondary"
                        icon={<UserPlus size={16} />}
                        size="small"
                        to="/console/$orgId/configuration/vendor-management/suppliers/list/details/$id/contacts/add"
                        params={{
                            orgId,
                            id,
                        }}
                    >
                        Add contact
                    </Button.Link>
                }
                onRowClick={(item, e) =>
                    navigate(
                        {
                            to: '/console/$orgId/configuration/vendor-management/suppliers/list/details/$id/contacts/edit/$contactId',
                            params: {
                                orgId,
                                id,
                                contactId: item.id,
                            },
                        },
                        e,
                    )
                }
                className="mt-4"
            />
        </PageController>
    );
}
